/**
 * Accessibility remediation - Searchanise ("snize") search results page.
 *
 * Searchanise renders the whole results page client-side and re-renders large
 * parts of it on every filter/sort change, so none of this markup can be fixed
 * in Liquid. Each patch below is idempotent, keyed off a data-attribute marker,
 * and re-applied by a MutationObserver after the app re-renders.
 *
 * Audit findings addressed:
 *   #114 - "Sort options" used role="button" on an <a href="#">, and the chosen
 *          option was never announced.
 *   #116 - decorative chevron beside "Filter" exposed a <title> description.
 *   #117 - "Filter" toggle was a plain <div>: no role, no name, no state, and
 *          not reachable by keyboard.
 *   #119 - "Reset filters" sat inside the applied-filters <ul>, so screen
 *          readers counted it as one of the applied filters.
 *   #122 - quick-view thumbnails had no role and no selected state.
 *   #124 - the quick-view info column is reordered with CSS `order`, so its
 *          reading order did not match the order it is painted in.
 *   #126 - activating "Add to cart" hid the focused button, dropping focus to
 *          <body> instead of moving it to the "View Cart" button that replaced it.
 *   #127 - quick-view errors (e.g. max quantity reached) had no live region.
 *   #129 - page title was just the query ("park - Mohawk General Store").
 *
 * Not addressed here (they need Searchanise-side changes - see the audit notes):
 *   #115 - focus ring contrast, handled in component-search.css.
 *   #121 - the "QUICK VIEW" <button> is nested inside the card's <a> wrapper.
 */
(function () {
  'use strict';

  var MARK = 'data-snize-a11y';

  function marked(el, key) {
    if (!el || el.getAttribute(MARK) === key) return true;
    el.setAttribute(MARK, key);
    return false;
  }

  function isVisible(el) {
    return !!el && el.getBoundingClientRect().height > 0;
  }

  /** Shared polite live region for announcements the app does not make itself. */
  var announcer;
  function announce(message) {
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.className = 'visually-hidden';
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      document.body.appendChild(announcer);
    }
    // Re-assigning the same string would not re-trigger the announcement.
    announcer.textContent = '';
    window.setTimeout(function () {
      announcer.textContent = message;
    }, 50);
  }

  /* #129 - descriptive page title. The theme serves "Search Results" (page_title
     for this Shopify page), then Searchanise replaces it with the bare query. We
     cannot fix this in Liquid: Shopify does not expose query-string parameters to
     it, so the search term only exists client-side. */
  var titleSuffix = null;

  /** The store name, for the "… - Mohawk General Store" tail. */
  function siteName() {
    // Served in the HTML, so this is already correct at DOMContentLoaded - unlike
    // document.title, which at that point is still the theme's bare "Search
    // Results" with no store name in it at all.
    var meta = document.querySelector('meta[property="og:site_name"]');
    var name = meta ? (meta.getAttribute('content') || '').trim() : '';
    if (name) return name;

    // No meta tag: fall back to the tail of whatever title is up. Match the whole
    // query so a " - " inside it (e.g. "wool - coat") is not mistaken for the
    // store-name delimiter.
    var query = new URLSearchParams(window.location.search).get('q') || '';
    var current = document.title;
    if (query && current.indexOf(query + ' - ') === 0) {
      return current.slice((query + ' - ').length).trim();
    }
    return current.split(' - ').slice(1).join(' - ').trim();
  }

  function fixPageTitle() {
    var query = new URLSearchParams(window.location.search).get('q');
    if (!query) return;

    // Resolve once, but only cache a real answer - an empty one must not stick,
    // or a page without the meta tag would lose the store name for good.
    if (!titleSuffix) titleSuffix = siteName();

    var wanted = 'Search results for "' + query + '"' + (titleSuffix ? ' - ' + titleSuffix : '');
    if (document.title !== wanted) document.title = wanted;
  }

  /* #117 + #116 - the "Filter" toggle and its decorative chevron. */
  function fixFilterToggle() {
    var toggle = document.querySelector('.snize-custom-filter-toggle');
    var sidebar = document.querySelector('.snize-filters-sidebar');
    if (!toggle || !sidebar) return;

    // #116 - the chevron duplicates the adjacent "Filter" text.
    var icons = toggle.querySelectorAll('svg');
    for (var i = 0; i < icons.length; i++) {
      var title = icons[i].querySelector('title');
      if (title) title.remove();
      icons[i].setAttribute('aria-hidden', 'true');
      icons[i].setAttribute('focusable', 'false');
    }

    if (!sidebar.id) sidebar.id = 'snize-filters-sidebar';

    var syncState = function () {
      toggle.setAttribute('aria-expanded', isVisible(sidebar) ? 'true' : 'false');
    };

    if (!marked(toggle, 'filter-toggle')) {
      toggle.setAttribute('role', 'button');
      toggle.setAttribute('tabindex', '0');
      toggle.setAttribute('aria-controls', sidebar.id);

      toggle.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
        event.preventDefault();
        toggle.click();
      });

      // display:none <-> block is the only signal the app gives us.
      if (window.MutationObserver) {
        new MutationObserver(syncState).observe(sidebar, {
          attributes: true,
          attributeFilter: ['style', 'class'],
        });
      }
      toggle.addEventListener('click', function () {
        window.setTimeout(syncState, 50);
      });
    }

    syncState();
  }

  /* #119 - "Reset filters" must not be a member of the applied-filters list. */
  function fixResetFilters() {
    var reset = document.querySelector('li.snize-clear-all-button');
    if (!reset) return;

    var list = reset.closest('ul');
    if (!list) return;

    // Move the original node (preserving Searchanise's own click handler) into a
    // wrapper placed after the list, so it is no longer a list item.
    var wrapper = document.createElement('div');
    wrapper.className = 'snize-clear-all-wrapper';
    list.parentNode.insertBefore(wrapper, list.nextSibling);
    wrapper.appendChild(reset);
  }

  /* #114 - "Sort options" control. A full select-only combobox needs
     Searchanise-side changes; what we can fix from here is the invalid
     role="button" on an <a href="#"> and the missing announcement of the
     chosen option. */
  function fixSortControl() {
    var sort = document.querySelector('a.snize-main-panel-dropdown-button[role="button"]');
    if (!sort) return;

    var currentLabel = function () {
      var value = (sort.textContent || '').replace(/\s+/g, ' ').trim();
      return value ? 'Sort options, ' + value : 'Sort options';
    };

    if (!marked(sort, 'sort-control')) {
      // role="button" on an <a href> is announced as a link by some screen
      // readers. Dropping href makes the role honest; Searchanise binds its own
      // click handler, so activation still works - but an <a> without href no
      // longer fires click from the keyboard, so wire that up.
      sort.removeAttribute('href');
      sort.setAttribute('tabindex', '0');
      sort.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
        event.preventDefault();
        sort.click();
      });
    }

    var label = currentLabel();
    if (sort.getAttribute('aria-label') !== label) {
      sort.setAttribute('aria-label', label);
    }
  }

  /* #122 - quick-view thumbnails: expose them as a tab list with a selected state.
     The audit asked for role="button" + a selected state, but aria-selected is not
     valid on a button, and picking one of several images is what role="tab" is for.

     Every thumbnail keeps tabindex="0". The APG tablist pattern would use a roving
     tabindex, but Searchanise ships all thumbnails focusable and handles Enter on
     them itself, and it provides no arrow-key navigation (verified: Arrow keys,
     Home and End all do nothing). Roving without arrow keys would leave every
     thumbnail except the selected one unreachable by keyboard - trading this 4.1.2
     finding for a 2.1.1 failure - so we add the semantics and leave the tab order
     Searchanise already gives us. */
  function fixQuickViewThumbnails(modal) {
    var list = modal.querySelector('.snize-quick-view-thumbnails');
    if (!list) return;

    list.setAttribute('role', 'tablist');
    list.setAttribute('aria-label', 'Product images');

    var syncTabs = function () {
      var items = list.querySelectorAll('.snize-quick-view-thumbnail');
      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        var control = item.querySelector('a') || item;
        var selected = item.classList.contains('active');

        // Only when the tab is an inner element does the <li> become
        // presentational; otherwise the <li> is itself the tab.
        if (control !== item) item.setAttribute('role', 'presentation');
        control.setAttribute('role', 'tab');
        control.setAttribute('aria-selected', selected ? 'true' : 'false');
        control.setAttribute('tabindex', '0');
      }
    };

    // Selecting a thumbnail only moves the `active` class - an attribute change.
    // The page-level observer in init() watches childList only, so it never fires
    // for this and aria-selected would report the wrong image for the rest of the
    // dialog's life. Watch the class itself. Nothing here writes `class`, so this
    // cannot feed itself.
    if (!marked(list, 'thumbnails') && window.MutationObserver) {
      new MutationObserver(syncTabs).observe(list, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true,
      });
    }

    syncTabs();
  }

  /* #126 - keep focus with the control that replaced the one we activated, and
     #127 - make quick-view errors announceable. */
  function fixQuickViewCart(modal) {
    if (marked(modal, 'quick-view')) return;
    if (!window.MutationObserver) return;

    var buttonText = function (el) {
      return (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
    };

    new MutationObserver(function () {
      // #127 - Searchanise injects the error node without any live semantics.
      var alerts = modal.querySelectorAll('[class*="error"], [class*="message"], [class*="notice"]');
      for (var i = 0; i < alerts.length; i++) {
        if (!alerts[i].hasAttribute('role')) alerts[i].setAttribute('role', 'alert');
      }

      // #126 - "Add to cart" is hidden and "View Cart" revealed in its place.
      // Hiding the focused button drops focus to <body>, so move it deliberately.
      var active = document.activeElement;
      if (active && active !== document.body && modal.contains(active) && isVisible(active)) return;

      var buttons = modal.querySelectorAll('button.snize-button');
      for (var b = 0; b < buttons.length; b++) {
        if (buttonText(buttons[b]) === 'view cart' && isVisible(buttons[b])) {
          buttons[b].focus();
          announce('Added to cart. View cart button available.');
          return;
        }
      }
    }).observe(modal, { childList: true, subtree: true, attributes: true });
  }

  /* #124 - the quick-view info column is a flex column whose children are
     reordered visually with CSS `order`: the vendor is painted above the title
     but sits second-to-last in the DOM, so scan mode reads the title, the price
     and the whole form before it ever reaches "Mijeong Park". Re-append the
     children in painted order - the `order` values then agree with the DOM, so
     nothing moves on screen and the reading order becomes the visual one.

     The `order` rules come from Searchanise's injected <style>, not from this
     repo, so this is the only place we can correct it. */
  function fixQuickViewReadingOrder(modal) {
    var column = modal.querySelector('.snize-quick-view-content-right');
    if (!column) return;

    var kids = [].slice.call(column.children);
    var painted = kids
      .map(function (el, i) {
        return { el: el, order: parseInt(window.getComputedStyle(el).order, 10) || 0, i: i };
      })
      // Stable: equal `order` keeps the DOM sequence, which is what flex paints.
      .sort(function (a, b) {
        return a.order - b.order || a.i - b.i;
      })
      .map(function (x) {
        return x.el;
      });

    // Re-appending always mutates childList, which would retrigger the page-level
    // observer and loop. Only touch the DOM when the order is actually wrong.
    for (var i = 0; i < kids.length; i++) {
      if (kids[i] !== painted[i]) {
        painted.forEach(function (el) {
          column.appendChild(el);
        });
        return;
      }
    }
  }

  /* #131 - quick-view size options. Searchanise already ships the right roles
     (radiogroup / radio / aria-checked / roving tabindex), but it applies them
     once at render and never again: choosing a size moves only the
     `snize-size-active` class, so aria-checked keeps naming the size the dialog
     opened with and the roving tabindex keeps pointing at it. And there is no
     arrow-key handling at all (verified: Arrow keys, Home and End do nothing),
     so with a roving tabindex every option except the checked one is
     unreachable by keyboard. Space does not select either. */
  function fixQuickViewSizes(modal) {
    var group = modal.querySelector('.snize-size-select-box');
    if (!group) return;

    // Searchanise keeps the same option nodes across a selection, but re-query
    // anyway so a re-render cannot leave us holding stale ones.
    var options = function () {
      return [].slice.call(group.querySelectorAll('.snize-size'));
    };

    var syncRadios = function () {
      var items = options();
      for (var i = 0; i < items.length; i++) {
        var checked = items[i].classList.contains('snize-size-active');
        items[i].setAttribute('aria-checked', checked ? 'true' : 'false');
        items[i].setAttribute('tabindex', checked ? '0' : '-1');
      }
    };

    if (!marked(group, 'sizes')) {
      group.addEventListener('keydown', function (event) {
        var items = options();
        var index = items.indexOf(document.activeElement);
        if (index === -1) return;

        var next;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          next = (index + 1) % items.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          next = (index - 1 + items.length) % items.length;
        } else if (event.key === 'Home') {
          next = 0;
        } else if (event.key === 'End') {
          next = items.length - 1;
        } else if (event.key === ' ' || event.key === 'Spacebar') {
          next = index;
        } else {
          return;
        }

        // Selection follows focus, as a radio group expects. Searchanise's own
        // click handler does the selecting; we only route the key to it.
        event.preventDefault();
        items[next].click();
        items[next].focus();
      });

      if (window.MutationObserver) {
        new MutationObserver(syncRadios).observe(group, {
          attributes: true,
          attributeFilter: ['class'],
          subtree: true,
        });
      }
    }

    syncRadios();
  }

  function fixQuickView() {
    var modal = document.getElementById('snize-modal-product-quick-view');
    if (!modal) return;
    fixQuickViewReadingOrder(modal);
    fixQuickViewThumbnails(modal);
    fixQuickViewSizes(modal);
    fixQuickViewCart(modal);
  }

  function applyAll() {
    fixPageTitle();
    fixFilterToggle();
    fixResetFilters();
    fixSortControl();
    fixQuickView();
  }

  function init() {
    if (!document.body.classList.contains('snize-results-page')) return;

    applyAll();

    if (!window.MutationObserver) return;

    // Searchanise re-renders in bursts; coalesce so we patch once per burst.
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = window.setTimeout(function () {
        pending = null;
        applyAll();
      }, 120);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
